<template>
    <div class="rounded-lgr border bg-surface-raised p-6" :class="isEditing ? 'border-control-border-focus ring-2 ring-control-halo' : 'border-border'">
        <template v-if="!isEditing">
            <div class="flex flex-col gap-5">
                <div class="flex items-center justify-between gap-4">
                    <div class="flex items-center gap-2.5 min-w-0">
                        <PerspectiveAvatar :name="displayName" :letters="perspectiveLetters" />
                        <div class="display-serif text-ink" translate="no">{{ displayName }}</div>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                        <span v-if="perspectiveTitle" class="inline-flex items-center gap-2 rounded-full bg-surface-sunken px-2.5 py-1 body-small text-ink">
                            <ArchetypeDot :letters="perspectiveLetters" class="size-3.5" />
                            {{ perspectiveTitle }}
                        </span>
                        <BaseButton
                            v-if="editable"
                            theme="neutral"
                            icon-only
                            :aria-label="t('components.hris.editBasicInfo')"
                            :title="t('components.hris.editBasicInfo')"
                            @click="isEditing = true">
                            <i class="bi bi-pencil text-sm/none" aria-hidden="true"></i>
                        </BaseButton>
                    </div>
                </div>

                <MarkdownText v-if="summary" :text="summary" class="body-small text-ink" />

                <HorizontalDivider class="my-1" />

                <div class="grid gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-3">
                    <div v-for="field in allFields" :key="field.key" class="info-field">
                        <div class="info-field-label">{{ field.label }}</div>
                        <div class="info-field-value">
                            {{ field.value }}<span v-if="field.suffix" class="ml-1 text-ink-muted">{{ field.suffix }}</span>
                        </div>
                    </div>
                    <slot name="extra-fields" :fields="facetFields">
                        <div v-for="field in facetFields" :key="field.facet_id" class="info-field">
                            <div class="info-field-label">{{ field.facet_name }}</div>
                            <div class="info-field-value">{{ field.value }}</div>
                        </div>
                    </slot>
                </div>
                <div v-if="lastSyncedLabel" class="flex items-center gap-2 details text-ink-muted">
                    <i class="bi bi-arrow-repeat text-xs" aria-hidden="true"></i>
                    {{ t("components.hris.lastSynced", { when: lastSyncedLabel }) }}
                </div>
            </div>
        </template>
        <template v-else>
            <ProfileEditForm
                :existing-public-profile="existingPublicProfile"
                :basic-info="basicInfo"
                :hris-data="hrisData"
                :default-profile-access="defaultProfileAccess"
                @saved="onSaved"
                @cancelled="isEditing = false" />
        </template>
    </div>
</template>

<script setup lang="ts">
import type { HrisData, OrgTreePerson } from "~vue/appTypes";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import ProfileEditForm from "~vue/components/ProfileEditForm.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import type { FacetValue } from "~vue/types/connection";
import { type ExistingPublicProfile } from "~vue/types/profile";
import { formatDate } from "~vue/utils/formatDate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import BaseButton from "./BaseButton.vue";
import HorizontalDivider from "./HorizontalDivider.vue";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        basicInfo: {
            full_name: string;
            role: string | null;
            team: string | null;
            level: string | null;
            organization: string | null;
        };
        editable?: boolean;
        existingPublicProfile?: ExistingPublicProfile | null;
        hrisData?: HrisData | null;
        manager?: OrgTreePerson | null;
        directReports?: OrgTreePerson[];
        perspectiveLetters?: string | null;
        facetFields?: FacetValue[];
        defaultProfileAccess?: string;
        summary?: string;
    }>(),
    {
        hrisData: null,
        summary: "",
        manager: null,
        directReports: () => [],
        perspectiveLetters: null,
        facetFields: () => [],
    },
);

const emit = defineEmits<{
    saved: [];
}>();

interface FieldEntry {
    key: string;
    label: string;
    value: string;
    suffix?: string;
}

const tenureLabel = computed(() => {
    if (!props.hrisData?.hire_date) {
        return null;
    }
    const hire = new Date(props.hrisData.hire_date);
    const now = new Date();
    let years = now.getFullYear() - hire.getFullYear();
    let months = now.getMonth() - hire.getMonth();
    if (months < 0) {
        years--;
        months += 12;
    }
    if (years > 0 && months > 0) {
        return t("components.hris.tenureYearsMonths", { years, months });
    }
    if (years > 0) {
        return t("components.hris.tenureYears", { count: years }, years);
    }
    if (months > 0) {
        return t("components.hris.tenureMonths", { count: months }, months);
    }
    return t("components.hris.lessThanMonth");
});

const startDateLabel = computed(() => {
    if (!props.hrisData?.start_date) {
        return null;
    }
    return formatDate(props.hrisData.start_date, "date-long");
});

const lastSyncedLabel = computed(() => {
    if (!props.hrisData?.last_synced_at) {
        return null;
    }
    const synced = new Date(props.hrisData.last_synced_at);
    const now = new Date();
    const diffMs = now.getTime() - synced.getTime();
    const diffMins = Math.floor(diffMs / 60_000);

    if (diffMins < 1) {
        return t("components.hris.justNow");
    }
    if (diffMins < 60) {
        return t("components.hris.minutesAgo", { count: diffMins });
    }
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
        return t("components.hris.hoursAgo", { count: diffHours });
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) {
        return t("components.hris.yesterday");
    }
    return t("components.hris.daysAgo", { count: diffDays });
});

const teamLabel = computed(() => (props.hrisData?.department && !props.existingPublicProfile?.division ? t("components.hris.department") : t("components.hris.team")));

const directReportsValue = computed(() => {
    const count = props.directReports.length;
    return count > 0 ? t("components.hris.directReportCount", { count }, count) : null;
});

const allFields = computed(() => {
    const role = props.existingPublicProfile?.title || props.hrisData?.job_title || props.basicInfo?.role;
    const team = props.existingPublicProfile?.division || props.hrisData?.department || props.basicInfo?.team;

    const candidates: (FieldEntry | null)[] = [
        role ? { key: "title", label: t("components.hris.role"), value: role } : null,
        team ? { key: "team", label: teamLabel.value, value: team } : null,
        props.basicInfo?.organization ? { key: "organization", label: t("components.hris.organization"), value: props.basicInfo.organization } : null,
        props.basicInfo?.level ? { key: "level", label: t("components.hris.level"), value: props.basicInfo.level } : null,
        props.existingPublicProfile?.email ? { key: "email", label: t("components.hris.email"), value: props.existingPublicProfile.email } : null,
        tenureLabel.value ? { key: "tenure", label: t("components.hris.tenure"), value: tenureLabel.value } : null,
        startDateLabel.value ? { key: "start_date", label: t("components.hris.startDate"), value: startDateLabel.value } : null,
        props.manager
            ? { key: "manager", label: t("components.hris.manager"), value: props.manager.name, suffix: props.manager.title ? `· ${props.manager.title}` : undefined }
            : null,
        directReportsValue.value ? { key: "direct_reports", label: t("components.hris.directReports"), value: directReportsValue.value } : null,
    ];

    return candidates.filter((f): f is FieldEntry => f !== null);
});

const displayName = computed(() => props.existingPublicProfile?.name || props.basicInfo?.full_name || t("components.profile.public.unknown"));

const perspectiveTitle = computed(() => (props.perspectiveLetters ? PERSPECTIVE_TYPES[props.perspectiveLetters]?.title : undefined));

const isEditing = ref(false);

function onSaved(): void {
    isEditing.value = false;
    emit("saved");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.info-field {
    @apply flex flex-col gap-1 min-w-0;
}

.info-field-label {
    @apply scaffold-body-small text-ink-muted;
}

.info-field-value {
    @apply body-semibold text-ink wrap-break-word;
}
</style>
